// Все операции над матрицей таблицы — одним местом. Раньше они жили в двух
// рендерящихся компонентах (TableSettings — строки/столбцы, TableElement — ячейка)
// и считали ширину по длине КАЖДОЙ строки: на рваных данных (импорт, легаси)
// «Удалить столбец» оставлял строку из нуля ячеек, и прямоугольник кликами уже
// не восстанавливался, а каждое сохранение отвечало 422.
//
// Инвариант: КАЖДАЯ операция приводит все строки к канонической ширине
// (`tableColumnCount`) — короткие добивает свежими пустыми ячейками, длинные режет.
// Поэтому рваная матрица чинится первой же операцией, а не портится дальше.
import type { DesignTableCell } from '@/types/blocks'
import { newTableCell, tableColumnCount } from './designElements'

export { tableColumnCount }

/** Строка ровно в `width` ячеек. Совпала по длине — возвращаем ТУ ЖЕ строку:
 *  на прямоугольных данных операции не пересобирают нетронутые строки. */
const toWidth = (row: DesignTableCell[], width: number): DesignTableCell[] =>
  row.length === width ? row : Array.from({ length: width }, (_, i) => row[i] ?? newTableCell())

const toRect = (data: DesignTableCell[][], width: number): DesignTableCell[][] =>
  data.map((row) => toWidth(row, width))

export const addTableRow = (data: DesignTableCell[][]): DesignTableCell[][] => {
  const width = tableColumnCount(data)
  // Стрелка обязательна: `Array.from` передаёт колбэку ИНДЕКС, и `newTableCell`
  // принял бы его за содержимое ячейки.
  return [...toRect(data, width), Array.from({ length: width }, () => newTableCell())]
}

/** Ширина — меньшая из «до» и «после» удаления. «До» держит счётчик столбцов от
 *  прыжка на прямоугольных данных (там обе величины равны), а ограничение сверху
 *  не даёт удалению СТРОКИ добавить столбцов: на рваных данных снятие самой широкой
 *  строки иначе раздуло бы всех выживших до её ширины. */
export const removeTableRow = (data: DesignTableCell[][]): DesignTableCell[][] => {
  const rest = data.slice(0, -1)
  return toRect(rest, Math.min(tableColumnCount(data), tableColumnCount(rest)))
}

export const addTableColumn = (data: DesignTableCell[][]): DesignTableCell[][] =>
  toRect(data, tableColumnCount(data) + 1)

/** Ниже одной ячейки не опускаемся: строка из нуля ячеек — это то самое
 *  состояние, из которого UI уже не выбирался (границу «не меньше двух»
 *  держит форма, здесь — только защита от вырожденных данных). */
export const removeTableColumn = (data: DesignTableCell[][]): DesignTableCell[][] =>
  toRect(data, Math.max(tableColumnCount(data) - 1, 1))

export const editTableCell = (
  data: DesignTableCell[][],
  rowIndex: number,
  colIndex: number,
  patch: Partial<DesignTableCell>,
): DesignTableCell[][] =>
  toRect(data, tableColumnCount(data)).map((row, ri) =>
    ri !== rowIndex ? row : row.map((cell, ci) => (ci !== colIndex ? cell : { ...cell, ...patch })),
  )
